import { datetimeLocalToIso, isoToDatetimeLocal } from './datetimeLocal';
import { kgToLbs, lbsToKg } from './units';
import {
  estimateCardioCalories,
  isCardioActivityType,
  normalizeActivityType,
  WEIGHTLIFTING,
} from './workoutActivity';
import { estimateResistanceCalories } from './workoutCalories';
import type { Workout } from '../types';

export function uid(): string {
  return crypto.randomUUID();
}

export type ExerciseMode = 'weighted' | 'bodyweight' | 'timed';

export type DraftSet = {
  key: string;
  reps: string;
  seconds: string;
  weight: string;
  rir: string;
  rpe: string;
  bodyweight: boolean;
  completed: boolean;
};

export type DraftExercise = {
  key: string;
  name: string;
  mode: ExerciseMode;
  showAdvanced: boolean;
  sets: DraftSet[];
};

export function emptySet(mode: ExerciseMode, template?: Partial<DraftSet>): DraftSet {
  return {
    key: uid(),
    reps: template?.reps ?? (mode === 'timed' ? '' : '10'),
    seconds: template?.seconds ?? (mode === 'timed' ? '45' : ''),
    weight: template?.weight ?? '',
    rir: template?.rir ?? '',
    rpe: template?.rpe ?? '',
    bodyweight: mode === 'bodyweight',
    completed: false,
    ...template,
  };
}

export function emptyExercise(name = ''): DraftExercise {
  return {
    key: uid(),
    name,
    mode: 'weighted',
    showAdvanced: false,
    sets: [emptySet('weighted'), emptySet('weighted')],
  };
}

export function parseDraftRir(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Math.round(Number(t));
  if (!Number.isFinite(n)) return null;
  return Math.min(6, Math.max(0, n));
}

export function parseDraftRpe(s: string): number | null {
  const t = s.trim();
  if (!t) return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return Math.min(10, Math.max(0, Math.round(n * 10) / 10));
}

export function isBodyweightHint(display: string): boolean {
  const t = display.trim().toLowerCase();
  if (!t) return false;
  return ['bw', 'body', 'b/w', 'bodyweight', 'bw-only', 'body weight'].includes(t);
}

export function sessionStartedToIso(localVal: string): string {
  return datetimeLocalToIso(localVal) ?? new Date().toISOString();
}

export function modeToTimeHold(mode: ExerciseMode): boolean {
  return mode === 'timed';
}

export function workoutToDrafts(
  w: Workout,
  imperial: boolean,
): {
  title: string;
  activityType: string;
  exercises: DraftExercise[];
  startedLocal: string;
  durationStr: string;
  notes: string;
} {
  const fromKg = (kg: number) => {
    if (!Number.isFinite(kg)) return '';
    if (imperial) return String(Math.round(kgToLbs(kg) * 10) / 10);
    return String(Math.round(kg * 10) / 10);
  };
  const activityType = normalizeActivityType(w.activityType);
  const exercises: DraftExercise[] =
    w.exercises.length > 0
      ? w.exercises.map((ex) => {
          const timeHold = ex.sets.some(
            (s) => s.trackMode === 'time' || ((s.durationSeconds ?? 0) > 0 && s.reps <= 0),
          );
          const allBw = ex.sets.length > 0 && ex.sets.every((s) => s.isBodyweight);
          const mode: ExerciseMode = timeHold ? 'timed' : allBw ? 'bodyweight' : 'weighted';
          return {
            key: uid(),
            name: ex.name,
            mode,
            showAdvanced: ex.sets.some((s) => s.rpe != null || s.rir != null),
            sets: ex.sets.map((s) => {
              const isTime =
                s.trackMode === 'time' || ((s.durationSeconds ?? 0) > 0 && s.reps <= 0);
              return {
                key: uid(),
                reps: isTime ? '' : String(s.reps),
                seconds:
                  isTime && s.durationSeconds != null && s.durationSeconds > 0
                    ? String(s.durationSeconds)
                    : isTime
                      ? '45'
                      : '',
                weight: s.isBodyweight ? '' : fromKg(s.weightKg),
                rir: s.rir != null ? String(s.rir) : '',
                rpe: s.rpe != null ? String(s.rpe) : '',
                bodyweight: Boolean(s.isBodyweight),
                completed: true,
              };
            }),
          };
        })
      : [];
  return {
    title: w.name,
    activityType,
    exercises,
    startedLocal: isoToDatetimeLocal(w.startedAt),
    durationStr: w.durationMinutes != null && w.durationMinutes > 0 ? String(w.durationMinutes) : '',
    notes: w.notes ?? '',
  };
}

export type LastPerformance = {
  weight: string;
  reps: string;
  seconds: string;
  rpe: string;
  mode: ExerciseMode;
};

export function findLastExercisePerformance(
  workouts: Workout[],
  exerciseName: string,
  imperial: boolean,
): LastPerformance | null {
  const key = exerciseName.trim().toLowerCase();
  if (!key) return null;
  const fromKg = (kg: number) => {
    if (!Number.isFinite(kg)) return '';
    if (imperial) return String(Math.round(kgToLbs(kg) * 10) / 10);
    return String(Math.round(kg * 10) / 10);
  };
  for (const w of workouts) {
    const ex = w.exercises.find((e) => e.name.trim().toLowerCase() === key);
    if (!ex || ex.sets.length === 0) continue;
    const s = ex.sets[ex.sets.length - 1]!;
    const isTime = s.trackMode === 'time' || ((s.durationSeconds ?? 0) > 0 && s.reps <= 0);
    const allBw = ex.sets.every((x) => x.isBodyweight);
    return {
      weight: s.isBodyweight ? '' : fromKg(s.weightKg),
      reps: isTime ? '' : String(s.reps),
      seconds: isTime && s.durationSeconds ? String(s.durationSeconds) : '',
      rpe: s.rpe != null ? String(s.rpe) : '',
      mode: isTime ? 'timed' : allBw ? 'bodyweight' : 'weighted',
    };
  }
  return null;
}

export function exerciseFromName(
  name: string,
  workouts: Workout[],
  imperial: boolean,
): DraftExercise {
  const last = findLastExercisePerformance(workouts, name, imperial);
  const mode = last?.mode ?? 'weighted';
  const template = last
    ? {
        weight: last.weight,
        reps: last.reps || '10',
        seconds: last.seconds || '45',
        rpe: last.rpe,
        bodyweight: mode === 'bodyweight',
      }
    : undefined;
  return {
    key: uid(),
    name,
    mode,
    showAdvanced: Boolean(last?.rpe),
    sets: [emptySet(mode, template), emptySet(mode, template)],
  };
}

export function buildPreviewPayload(
  exercises: DraftExercise[],
  prefersMetric: boolean,
  profileKg: number | null | undefined,
) {
  const toKg = (display: string) => {
    const n = Number(display);
    if (!Number.isFinite(n) || n < 0) return 0;
    return prefersMetric ? n : lbsToKg(n);
  };
  return exercises
    .filter((ex) => ex.name.trim())
    .map((ex, i) => ({
      name: ex.name.trim(),
      muscleGroup: 'general',
      sortOrder: i,
      sets: ex.sets.map((s, j) => {
        const bw = ex.mode === 'bodyweight' || s.bodyweight || isBodyweightHint(s.weight);
        const hasProfile = profileKg != null && profileKg > 0 && Number.isFinite(profileKg);
        const weightKg = bw && hasProfile ? profileKg! : bw ? 0 : toKg(s.weight);
        if (ex.mode === 'timed') {
          const sec = Math.round(Number(s.seconds));
          const durationSeconds =
            Number.isFinite(sec) && sec > 0 ? Math.min(7200, Math.max(1, sec)) : null;
          return {
            trackMode: 'time' as const,
            reps: 0,
            durationSeconds,
            weightKg,
            isBodyweight: bw,
            setIndex: j,
            rir: parseDraftRir(s.rir),
            rpe: parseDraftRpe(s.rpe),
          };
        }
        return {
          trackMode: 'reps' as const,
          reps: Math.max(0, Math.round(Number(s.reps) || 0)),
          durationSeconds: null,
          weightKg,
          isBodyweight: bw,
          setIndex: j,
          rir: parseDraftRir(s.rir),
          rpe: parseDraftRpe(s.rpe),
        };
      }),
    }));
}

export function estimateSessionKcal(
  activityType: string,
  exercises: DraftExercise[],
  profileKg: number | null | undefined,
  durationMinutes: number | null,
  prefersMetric: boolean,
): number {
  const cardioMode = isCardioActivityType(activityType);
  if (cardioMode) {
    return estimateCardioCalories(profileKg, durationMinutes, activityType);
  }
  const payload = buildPreviewPayload(exercises, prefersMetric, profileKg);
  return estimateResistanceCalories(profileKg, payload.map((e) => ({ sets: e.sets })), {
    durationMinutes,
  });
}

export function sessionHasWork(exercises: DraftExercise[], cardioMode: boolean): boolean {
  if (cardioMode) return true;
  const named = exercises.filter((ex) => ex.name.trim());
  if (named.length === 0) return false;
  return named.some((ex) =>
    ex.sets.some((s) => {
      if (ex.mode === 'timed') {
        const sec = Math.round(Number(s.seconds));
        return Number.isFinite(sec) && sec > 0;
      }
      return Math.max(0, Math.round(Number(s.reps) || 0)) > 0;
    }),
  );
}

export function defaultSessionState(editWorkout?: Workout | null, imperial?: boolean) {
  if (editWorkout) {
    const d = workoutToDrafts(editWorkout, imperial ?? false);
    return {
      workoutName: d.title,
      activityType: d.activityType,
      sessionStartedLocal: d.startedLocal,
      durationMinutesStr: d.durationStr,
      sessionNotes: d.notes,
      exercises: d.exercises.length > 0 ? d.exercises : [],
    };
  }
  return {
    workoutName: '',
    activityType: WEIGHTLIFTING,
    sessionStartedLocal: isoToDatetimeLocal(new Date().toISOString()),
    durationMinutesStr: '',
    sessionNotes: '',
    exercises: [] as DraftExercise[],
  };
}

export function formatSetPrevious(
  last: LastPerformance | null,
  setIndex: number,
  imperial: boolean,
): string {
  if (!last) return '—';
  const unit = imperial ? 'lb' : 'kg';
  if (last.mode === 'timed') return last.seconds ? `${last.seconds}s` : '—';
  if (last.mode === 'bodyweight') return last.reps ? `${last.reps} reps` : '—';
  if (last.weight && last.reps) return `${last.weight} ${unit} × ${last.reps}`;
  return '—';
}

export function hapticLight() {
  void import('./haptics').then((m) => m.hapticLight());
}

export function hapticSuccess() {
  void import('./haptics').then((m) => m.hapticSuccess());
}
